import { GroupedInsights } from '@toggle/chart';
import { isToday } from 'date-fns';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useInsightDrawer } from '~/hooks/use-insight-drawer/useInsightDrawer';
import { getExploreDirection } from '~/views/dashboard/whats-new-widget/components/insight-card/utils';
import { getConditionIcons } from '~/views/insights/components/insight-icons/utils';
import { InsightIconsRoot } from '~/views/insights/components/insight-icons-root/InsightIconsRoot';
import { InsightsTooltip } from '~/views/insights/components/insights-tooltip/InsightsTooltip';

import * as S from './ChartInsightIcon.styles';

interface ChartInsightIconProps {
  groupedInsights: GroupedInsights;
  insightIconRight: number;
  insightIconTop: number;
  isSimpleChart?: boolean;
}

const ICON_SCALE = 1.25;
const ICON_Y_AXIS_MARGIN = 4;

export const ChartInsightIcon: FC<ChartInsightIconProps> = ({
  groupedInsights,
  insightIconTop,
  insightIconRight,
  isSimpleChart,
}) => {
  const { t } = useTranslation(['chart']);
  const { openDrawer } = useInsightDrawer();

  const singleIconWidth = isSimpleChart ? 12 : 24;
  const singleIconHeight = isSimpleChart ? 14 : 28;

  const verticalCombinedWidth = isSimpleChart ? 12 : 24;
  const verticalCombinedHeight = isSimpleChart ? 24 : 47;

  return (
    <>
      {groupedInsights.insights.map((insight, idx) => {
        const isStarred = isToday(new Date(insight.created_at));
        const conditions = getConditionIcons({
          kind: insight.metadata.kind,
          conditions: insight.metadata.conditions,
        });
        const isCombined = conditions.length > 1;

        const iconWidth = isCombined ? verticalCombinedWidth : singleIconWidth;
        const iconHeight = isCombined
          ? verticalCombinedHeight
          : singleIconHeight;
        const iconSize = { width: `${iconWidth}px`, height: `${iconHeight}px` };

        const iconRight = insightIconRight - iconWidth / 2;
        const iconOffsetY = idx * iconHeight * ICON_SCALE;
        const iconY =
          insightIconTop - iconHeight - ICON_Y_AXIS_MARGIN - iconOffsetY;
        const episodeLabel = t('chart:insightCard.episode', {
          count: insight.metadata.episode_count,
        });

        return (
          <InsightsTooltip
            key={insight.id}
            conditions={conditions}
            direction={getExploreDirection(insight.metadata.direction)}
            customSize={{ width: '27px', height: '32px' }}
            isStarred={isToday(new Date(insight.created_at))}
            isMatched
            horizon={insight.metadata.horizon}
            median={insight.metadata.median_return}
            stars={insight.metadata.stars}
            name={insight.card.name}
            entityTag={insight.metadata.entity}
            episodeLabel={episodeLabel}
          >
            <S.IconContainer
              key={insight.id}
              $top={iconY}
              $right={iconRight}
              onClick={() => openDrawer(insight.id, { source: 'chart' })}
            >
              <InsightIconsRoot
                conditions={conditions}
                direction={getExploreDirection(insight.metadata.direction)}
                customSize={iconSize}
                isStarred={isStarred}
                isCombined={isCombined}
                layout="vertical"
                isSimpleChart={isSimpleChart}
              />
            </S.IconContainer>
          </InsightsTooltip>
        );
      })}
    </>
  );
};
